import * as openpgp from "openpgp";
import { IDecryptedKeys } from "./decryptKeys";
import { decryptPrivateKey } from "./decryptPrivateKey";
import { readAsBase64 } from "./readAsBase64";
import { readFileFromBase64 } from "./readFileFromBase64";

/**
 * Przeszyfrowuje klucz obiektu na POWIĘKSZONĄ pulę odbiorców: odszyfrowuje go kluczem
 * bieżącego użytkownika i szyfruje ponownie jednym blobem PGP na wszystkie podane klucze
 * publiczne (dotychczasowi odbiorcy + nowy uczestnik).
 *
 * To jedyny moment, w którym klucz obiektu istnieje w postaci jawnej — z założenia
 * wyłącznie w przeglądarce uczestnika, który już ma do niego dostęp. Backend nie ma
 * materiału prywatnego i nie może wykonać tej operacji za nas.
 *
 * Logika żyła dotąd wyłącznie inline w components/KeysManager.
 */
export const reEncryptObjectKeyForPublicKeys = async (
  encryptedPrivateKeyBase64: string,
  myKeys: IDecryptedKeys,
  publicKeysBase64: (string | null | undefined)[],
): Promise<string> => {
  const decrypted = await decryptPrivateKey(myKeys, {
    Id: "",
    PublicKey: undefined,
    PrivateKey: encryptedPrivateKeyBase64,
    Armored: false,
    Initial: false,
    ParentKeyId: undefined,
  });

  // W puli mieszają się dwa formaty: klucze intermediate użytkowników to base64
  // ASCII-armored, a klucze stron zewnętrznych — base64 binarnego packetlistu.
  //
  // Operacja jest FAIL-CLOSED i musi taka zostać. Backend po otrzymaniu wyniku
  // BEZWARUNKOWO nadpisuje ObjectKey.PrivateKey i zamyka żądanie recovery — jeśli
  // wypuścilibyśmy tu pulę uboższą o klucz, którego nie udało się sparsować, jego
  // właściciel straciłby dostęp do konwersacji bez żadnego śladu. Lepiej przerwać
  // i zostawić klucz obiektu nietknięty.
  //
  // UWAGA: w openpgp 4.10 ani key.readArmored, ani key.read NIE rzucają przy błędzie
  // parsowania — zwracają { keys: [], err: [...] }. Sam try/catch by tu nie wystarczył.
  const publicKeys = await Promise.all(
    (publicKeysBase64 ?? []).map(async (k, index) => {
      if (!k) {
        throw new Error(
          `Pusty klucz publiczny na pozycji ${index} — przerywam przeszyfrowanie`,
        );
      }

      const keyBuffer = await readFileFromBase64(k, "application/other");

      try {
        const armored = new TextDecoder().decode(keyBuffer);
        const key = await openpgp.key.readArmored(armored);

        if (key.keys.length) return key.keys[0];
      } catch {
        // Nie armored — próbujemy odczytu binarnego niżej.
      }

      const notArmoredKey = await openpgp.key.read(new Uint8Array(keyBuffer));

      if (!notArmoredKey.keys.length) {
        throw new Error(
          `Nie udało się odczytać klucza publicznego na pozycji ${index} — przerywam przeszyfrowanie`,
        );
      }

      return notArmoredKey.keys[0];
    }),
  );

  if (!publicKeys.length) {
    throw new Error("Pusta pula kluczy publicznych — nie ma dla kogo szyfrować");
  }

  const { message } = await openpgp.encrypt({
    message: await openpgp.message.fromBinary(decrypted.data),
    publicKeys,
    armor: false,
  });

  return await readAsBase64(new Uint8Array(message.packets.write()));
};
